import { CityAutocomplete } from "./CityAutocomplete";
import { useRef, useState, type FormEvent } from "react";
import { apiFetch } from "../api/client";
import { useLocale } from "../contexts/LocaleContext";
import { useToast } from "../contexts/ToastContext";
import { loadGoogleMaps } from "../earth/googleMapsLoader";
import { COUNTRY_CODES } from "../lib/countries";
import type { UserResponse } from "../pages/ProfilePage";

const fields = [
  ["name", "artistName", "nickname"],
  ["donationFirstName", "firstName", "given-name"],
  ["donationLastName", "lastName", "family-name"],
  ["donationAddress", "street", "street-address"],
  ["donationPostalCode", "postalCode", "postal-code"],
  ["donationCity", "city", "address-level2"],
  ["donationCountry", "country", "country"],
] as const;
type Field = typeof fields[number][0];
type TextField = Field | "state" | "city" | "artistType" | "otherArtistType" | "birthdate" | "gender";
type Draft = Record<TextField, string> & { isArtist: boolean; donationCert: boolean };
const artistOptions = [["Maler", "painter"], ["Bildhauer", "sculptor"], ["Fotograf", "photographer"], ["Sonstiges", "other"]] as const;

export function ProfileDetailsForm({ user, onSaved, onEditingChange }: { user: UserResponse; onSaved: (user: UserResponse) => void; onEditingChange?: (editing: boolean) => void }) {
  const { t, formatDate } = useLocale();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>(() => ({
    ...Object.fromEntries(fields.map(([key]) => [key, key === "name" ? user.name || "" : user.settings?.[key] || ""])) as Record<Field, string>,
    state: user.settings?.state || user.settings?.donationCountry || "",
    city: user.settings?.city || user.settings?.donationCity || "",
    isArtist: user.settings?.isArtist ?? !!user.settings?.artistType,
    artistType: user.settings?.artistType || "",
    otherArtistType: user.settings?.otherArtistType || "",
    donationCert: user.settings?.donationCert ?? false,
    birthdate: user.settings?.birthdate || "",
    gender: user.settings?.gender || "",
  }));
  const [saving, setSaving] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<google.maps.GeocoderResult[]>([]);
  const requestId = useRef(0);

  const change = (key: keyof Draft, value: string | boolean) => {
    setDraft(previous => ({ ...previous, [key]: value }));
    setConfirmed(false);
  };
  const finishEditing = () => { setEditing(false); onEditingChange?.(false); };
  const countrySelect = (key: "state" | "donationCountry") => <select
    required={key === "state"} autoComplete={key === "state" ? undefined : "country"}
    value={draft[key]} onChange={event => {
      change(key, event.target.value);
      if (key === "state") change("city", "");
    }}>
    <option value="">{t("register.selectCountry")}</option>
    {draft[key] && !COUNTRY_CODES.some(code => code === draft[key]) && <option value={draft[key]}>{draft[key]}</option>}
    {COUNTRY_CODES.map(code => <option key={code} value={code}>{t(`register.countries.${code}`)}</option>)}
  </select>;
  const textInput = (key: TextField, autoComplete?: string) => <input
    type={key === "birthdate" ? "date" : "text"} required={key === "name" || key === "city"}
    maxLength={key === "donationPostalCode" ? 32 : 255} autoComplete={autoComplete}
    value={draft[key]} onChange={event => change(key, event.target.value)} />;

  const searchAddress = async () => {
    if (!query.trim()) return;
    const id = ++requestId.current;
    setSearching(true);
    setResults([]);
    try {
      await loadGoogleMaps();
      const response = await new google.maps.Geocoder().geocode({ address: query.trim() });
      if (id !== requestId.current) return;
      setResults(response.results);
      if (!response.results.length) toast.show(t("profile.noAddress"), "info");
    } catch {
      if (id === requestId.current) toast.show(t("profile.addressFailed"), "danger");
    } finally {
      if (id === requestId.current) setSearching(false);
    }
  };

  const selectAddress = (result: google.maps.GeocoderResult) => {
    const part = (type: string, short = false) => {
      const component = result.address_components.find(c => c.types.includes(type));
      return (short ? component?.short_name : component?.long_name) || "";
    };
    setDraft(prev => ({ ...prev,
      donationAddress: [part("route") || part("premise"), part("street_number")].filter(Boolean).join(" "),
      donationPostalCode: part("postal_code"),
      donationCity: part("locality") || part("postal_town") || part("administrative_area_level_3"),
      donationCountry: part("country", true),
    }));
    setResults([]);
    setQuery(result.formatted_address);
    setConfirmed(false);
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (saving || !confirmed) return;
    setSaving(true);
    try {
      const response = await apiFetch("/user/profile", { method: "PUT", body: JSON.stringify(draft) });
      if (response.status === 409) {
        const latest = await apiFetch("/user");
        if (latest.ok) onSaved({ ...user, ...await latest.json() });
        toast.show(t("profile.alreadyEdited"), "info");
        finishEditing();
        return;
      }
      if (!response.ok) throw new Error(String(response.status));
      // Profile updates can omit identity fields such as the verified phone.
      onSaved({ ...user, ...await response.json() });
      finishEditing();
      toast.show(t("profile.saved"), "success");
    } catch {
      toast.show(t("profile.editFailed"), "danger");
    } finally { setSaving(false); }
  };

  if (user.profile_updated_at) return <p>{t("profile.editedOn", { date: formatDate(user.profile_updated_at) })}</p>;
  // Older backend versions must not offer an edit they cannot enforce safely.
  if (user.profile_updated_at === undefined) return null;
  if (!editing) return <button className="profile-edit-button" type="button" onClick={() => { setEditing(true); onEditingChange?.(true); setConfirmed(false); }}>{t("profile.editOnce")}</button>;

  return <form className="profile-edit-form" onSubmit={save}>
    <p>{t("profile.onceNotice")}</p>
    <fieldset disabled={saving}>
      <legend>{t("profile.personalData")}</legend>
      <h3 className="profile-edit-section">{t("profile.identitySection")}</h3>
      <div className="profile-edit-fields">
        {fields.slice(0, 3).map(([key, label, autocomplete]) => <label key={key}>{t(`profile.${label}`)}{textInput(key, autocomplete)}</label>)}
        <label>{t("register.country")}{countrySelect("state")}</label>
        <label>{t("register.city")}<CityAutocomplete country={draft.state} value={draft.city} onChange={value => change("city", value)} name="city" /></label>
      </div>
      <label className="profile-check"><input type="checkbox" checked={draft.isArtist} onChange={event => change("isArtist", event.target.checked)} />{t("register.isArtist")}</label>
      {draft.isArtist && <div className="profile-edit-fields">
        <label>{t("register.artistType")}<select value={draft.artistType} onChange={event => change("artistType", event.target.value)}>
          <option value="">{t("register.selectArtistType")}</option>
          {draft.artistType && !artistOptions.some(([value]) => value === draft.artistType) && <option value={draft.artistType}>{draft.artistType}</option>}
          {artistOptions.map(([value, label]) => <option key={value} value={value}>{t(`register.${label}`)}</option>)}
        </select></label>
        {draft.artistType === "Sonstiges" && <label>{t("register.otherSpecify")}{textInput("otherArtistType")}</label>}
      </div>}
      <h3 className="profile-edit-section">{t("profile.address")}</h3>
      <label className="profile-check"><input type="checkbox" checked={draft.donationCert} onChange={event => change("donationCert", event.target.checked)} />{t("register.donationCert")}</label>
      <label>{t("profile.searchAddress")}
        <input type="search" value={query} placeholder={t("profile.addressExample")} onChange={e => {
          setQuery(e.target.value); requestId.current++; setSearching(false); setResults([]);
        }} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); void searchAddress(); } }} />
      </label>
      <div className="profile-edit-actions"><button type="button" disabled={searching || !query.trim()} onClick={() => void searchAddress()}>{t(searching ? "profile.searching" : "profile.searchAddress")}</button></div>
      <ul className="profile-address-results" aria-live="polite">{results.map((result, index) => <li key={result.place_id || index}>
        <button type="button" onClick={() => selectAddress(result)}>{result.formatted_address}</button>
      </li>)}</ul>
      <div className="profile-edit-fields">{fields.slice(3).map(([key, label, autocomplete]) => <label key={key}>
        {t(`profile.${label}`)}
        {key === "donationCountry" ? countrySelect(key) : textInput(key, autocomplete)}
      </label>)}</div>
      <h3 className="profile-edit-section">{t("profile.morePersonal")}</h3>
      <div className="profile-edit-fields">
        <label>{t("register.birthdate")}{textInput("birthdate", "bday")}</label>
        <label>{t("register.gender")}<select value={draft.gender} onChange={event => change("gender", event.target.value)}>
          <option value="">{t("register.genderNone")}</option>
          <option value="m">{t("register.genderMale")}</option>
          <option value="w">{t("register.genderFemale")}</option>
          <option value="d">{t("register.genderDiverse")}</option>
        </select></label>
      </div>
      <p><label><span><input type="checkbox" name="confirmEdit" required checked={confirmed} onChange={e => setConfirmed(e.target.checked)} /> {t("profile.confirmEdit")}</span></label></p>
      <div className="profile-edit-actions">
        <button type="submit" disabled={!confirmed || saving}>{t(saving ? "profile.saving" : "profile.save")}</button>
        <button type="button" onClick={() => { requestId.current++; setSearching(false); setResults([]); finishEditing(); }}>{t("profile.cancel")}</button>
      </div>
    </fieldset>
  </form>;
}
